/**
 * API Response Type Definitions
 *
 * Centralized type definitions for all API responses.
 * Ensures type safety across the application.
 *
 * NOTE: Backend returns camelCase via db-mappers.ts transformation.
 * All property names should use camelCase to match the transformed data.
 */

// Import network types from api-contracts
import type { NetworkNode, NetworkEdge } from './api-contracts';

// ============================================================================
// CORE API TYPES
// ============================================================================

/**
 * Standard API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * Paginated API response
 */
export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  total: number;
  limit?: number;
  offset?: number;
  hasMore: boolean;
}

// ============================================================================
// CARRIER TYPES
// ============================================================================

/**
 * Carrier profile from database
 */
export interface CarrierProfile {
  dotNumber: number;
  legalName: string;
  dbaName: string | null;
  phyStreet: string | null;
  phyCity: string | null;
  phyState: string | null;
  phyZip: string | null;
  phyZipcode?: string | null;  // Alias
  phyCountry?: string | null;
  phone: string | null;
  email?: string | null;
  emailAddress: string | null;
  statusCode: string;
  allowedToOperate: string | null;
  safetyRating: string | null;
  safetyRatingDate: string | null;
  totalPowerUnits: number;
  totalDrivers: number;
  driverInsp?: number;
  driverOosInsp?: number;
  driverOosRate: string | null;
  driverOosRateNationalAverage?: string | null;
  vehicleInsp?: number;
  vehicleOosInsp?: number;
  vehicleOosRate: string | null;
  vehicleOosRateNationalAverage?: string | null;
  crashTotal: number;
  fatalCrash: number;
  injCrash?: number;
  towawayCrash?: number;
  issScore: number | null;
  mcs150Date: string | null;
  // Insurance fields
  bipdInsuranceOnFile?: string | number | null;
  bipdInsuranceRequired?: string | number | null;
  bipdRequiredAmount?: string | number | null;
  cargoInsuranceOnFile?: string | number | null;
  cargoInsuranceRequired?: string | number | null;
  bondInsuranceOnFile?: string | number | null;
  bondInsuranceRequired?: string | number | null;
  // MC and authority
  mcNumber?: string;
  mxNumber?: string;
  ffNumber?: string;
  commonAuthorityStatus?: string;
  contractAuthorityStatus?: string;
  brokerAuthorityStatus?: string;
  // Other fields
  ein?: number;
  censusTypeId?: {
    censusType: string;
    censusTypeDesc: string;
    censusTypeId: number;
  };
  carrierOperation?: {
    carrierOperationCode: string;
    carrierOperationDesc: string;
  };
  createdAt?: string;
  updatedAt?: string;
  // DNU (Do Not Use) status
  dnuStatus?: {
    shipper: string;
    dnuDate: string;
    listFile: string;
  } | null;
}

/**
 * Search result for carrier search
 */
export interface SearchResult {
  dotNumber: number;
  dot_number?: number;  // Snake_case alias
  legalName: string;
  legal_name?: string;  // Snake_case alias
  dbaName: string | null;
  dba_name?: string | null;  // Snake_case alias
  phyCity: string | null;
  phy_city?: string | null;  // Snake_case alias
  phyState: string | null;
  phy_state?: string | null;  // Snake_case alias
  statusCode: string;
  safetyRating: string | null;
  safety_rating?: string | null;  // Snake_case alias
  totalPowerUnits: number;
  total_power_units?: number;  // Snake_case alias
  matchType?: 'dot' | 'mc' | 'name' | 'legal_name' | 'dba_name';
  relevanceScore?: number;
  // Insurance fields (for advanced search results)
  bipd_cancellation_date?: string;
  bipd_policy_effective_date?: string;
  bipd_posted_date?: string;
  cargo_cancellation_date?: string;
  cargo_policy_effective_date?: string;
  cargo_posted_date?: string;
}

// ============================================================================
// INSPECTION TYPES
// ============================================================================

/**
 * Inspection record from database
 */
export interface Inspection {
  inspectionId: string;
  dotNumber: number;
  carrier_dot?: number;  // Snake_case alias for dotNumber
  carrier_name?: string;
  legalName?: string;
  inspDate: string;
  inspectionDate?: string;  // Alias for inspDate used by components
  inspLevelId: number;
  inspLevel?: string;
  inspStartTime?: string;
  inspEndTime?: string;
  insp_start_time?: string;  // Snake_case alias for legacy code
  insp_end_time?: string;    // Snake_case alias for legacy code
  violTotal: number;
  totalViolations?: number;  // Alias for violTotal used by components
  oosTotal: number;
  oosViolations?: number;  // Alias for oosTotal used by components
  driverViolTotal: number;
  driverOosTotal: number;
  vehicleViolTotal: number;
  vehicleOosTotal: number;
  hmViolTotal?: number;
  hazmatViolTotal?: number;  // Alias for hmViolTotal
  hazmatOosTotal?: number;
  postAccInd?: string;
  post_acc_ind?: string;  // Snake_case alias
  grossCombVehWt?: number;  // Gross combined vehicle weight in lbs
  gross_comb_veh_wt?: number;  // Snake_case alias
  shipperName?: string;  // Shipper name at time of inspection
  shipper_name?: string;  // Snake_case alias
  inspCarrierName?: string;  // Carrier name at time of inspection
  insp_carrier_name?: string;  // Snake_case alias
  inspInterstate?: string;  // Interstate status (Y/N)
  insp_interstate?: string;  // Snake_case alias
  state?: string;
  reportState?: string;  // Alias for state
  city?: string;
  county?: string;
  countyCode?: string;
  county_code?: string;  // Snake_case alias
  locationDesc?: string | null;
  location_desc?: string | null;  // Snake_case alias for legacy code
  latitude?: number | null;
  longitude?: number | null;
  timeWeight?: number;
  reportNumber?: string;
  vins?: string[];
  vin?: string;  // Single VIN alias for components
  vehicleVin?: string;  // Vehicle VIN alias for components
  vehicleIdentificationNumber?: string;  // Full property name
  vehicleLicense?: string;
  vehicleLicenseState?: string;
  licenseState?: string;  // Alias
  vehicleMake?: string;
  licensePlate?: string;
  unitType?: string;
  veh_unit_type?: string;  // Snake_case alias
  primaryVin?: string | null;
}

// ============================================================================
// VIOLATION TYPES
// ============================================================================

/**
 * Violation record from database
 */
export interface Violation {
  inspectionId: string;
  inspectionNumber?: string;
  dotNumber?: number;
  carrier_dot?: number;  // Snake_case alias for dotNumber
  carrier_name?: string;
  legalName?: string;
  inspDate: string;
  inspectionDate?: string;  // Alias for inspDate used by components
  changeDate?: string;  // For violation date tracking
  partNo: string;
  part_no?: string;  // Snake_case alias
  partNoSection: string;
  part_no_section?: string;  // Snake_case alias
  inspViolUnit?: string;
  basicCategory: string;
  severityWeight: number;
  severity?: string;  // String severity level (HIGH, MEDIUM, LOW)
  outOfServiceIndicator: string;
  outOfService?: boolean;  // Boolean alias
  violationCode: string;
  violation_code?: string;  // Snake_case alias
  description?: string;
  violation_description?: string;  // Snake_case alias for description
  oosWeight?: number;
  timeWeight?: number;
  totalWeight?: number;
  weightedSeverity?: number;
  state?: string;
  inspectionState?: string;  // Alias for state used by components
  reportState?: string;  // Alias for state
  city?: string;
  county?: string;
  countyCode?: string;
  county_code?: string;  // Snake_case alias
  location_desc?: string;  // Location description
  vin?: string;
  vehicleVin?: string;
  vehicleMake?: string;
  vehicleLicense?: string;
  cfrPart?: string;
  cfr_part?: string;  // Snake_case alias
  unitType?: string;
  unitMake?: string;
  allUnits?: string[];
  inspectionLocation?: string;
  inspViolationId?: string;  // Violation ID
  isDriver?: boolean;
  isVehicle?: boolean;
  isOos?: boolean;
  oos?: boolean;  // Alias for isOos
  isHazmat?: boolean;
  is_hazmat?: boolean;  // Snake_case alias
  isCritical?: boolean;
  isMinor?: boolean;
  monthsAgo?: number;
}

// ============================================================================
// CRASH TYPES
// ============================================================================

/**
 * Crash record from database
 */
export interface Crash {
  crashId: string;
  crash_id?: string;  // Snake_case alias
  dotNumber: number;
  reportDate: string;
  reportNumber?: string;
  reportTime?: string;
  reportState?: string;  // Alias for state used by components
  report_state?: string;  // Snake_case alias
  towAway: string;
  hazmatReleased?: string;  // 'Y' or 'N'
  fatalities: number;
  injuries: number;
  totalVehicles?: number;
  sequenceNumber?: number;
  weatherConditionId?: number;
  weather_condition_id?: number;  // Snake_case alias
  weatherCondition?: string;
  weather_condition?: string;  // Snake_case alias
  roadSurfaceConditionId?: number;
  road_surface_condition_id?: number;  // Snake_case alias
  roadSurfaceCondition?: string;
  roadCondition?: string;  // Alias
  road_condition?: string;  // Snake_case alias
  lightConditionId?: number;
  light_condition_id?: number;  // Snake_case alias
  lightCondition?: string;
  light_condition?: string;  // Snake_case alias
  vehicleConfigurationId?: number;
  vehicle_configuration_id?: number;  // Snake_case alias
  vehicleConfiguration?: string;
  cargoBodyTypeId?: number;
  cargoBodyType?: string;
  state?: string;
  city?: string;
  county?: string;
  countyCode?: string;
  county_code?: string;  // Snake_case alias
  location?: string;
  severity?: 'FATAL' | 'INJURY' | 'TOWAWAY';
  timeWeight?: number;
  vin?: string;
  vehicleIdentificationNumber?: string;
  vehicleMake?: string;
  vehicleLicense?: string;
  vehicleLicenseState?: string;
  carrier_dot?: number;  // Alias for dotNumber
  towAwayCount?: number;
}

// ============================================================================
// VEHICLE TYPES
// ============================================================================

/**
 * Vehicle/VIN record
 */
export interface Vehicle {
  vin: string;
  dotNumber?: number;
  vinStatus: 'VALID' | 'INVALID' | 'UNKNOWN';
  year?: number;
  make?: string;
  model?: string;
  vehicleType?: string;
  vehicleMake?: string;  // Alias for make
  vehicleModel?: string;  // Alias for model
  unitType?: string;  // Type of unit (truck, trailer, etc.)
  firstSeenDate?: string;
  lastSeenDate?: string;
  inspectionCount?: number;
  carrierCount?: number;
  // Decoded vehicle info from NHTSA
  isDecoded?: boolean;
  modelYear?: number;
  bodyClass?: string;
  // Crash statistics
  crashCount?: number;
  totalFatalities?: number;
  totalInjuries?: number;
  lastCrashDate?: string;
  lastCrashState?: string;
  towAwayCount?: number;
}

/**
 * VIN match result
 */
export interface VinMatch {
  vin: string;
  carriers: VinCarrierMatch[];
  totalCarriers: number;
  shellGameDetected: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  isRentalUnit: boolean;
}

export interface VinCarrierMatch {
  dotNumber: number;
  legalName: string;
  firstSeen: string;
  lastSeen: string;
  inspectionCount: number;
  violationCount?: number;
  oosCount?: number;
}

// ============================================================================
// CENSUS DATA TYPES
// ============================================================================

/**
 * FMCSA Census data
 */
export interface CensusData {
  dotNumber: number;
  legalName: string;
  dbaName: string | null;
  addDate: string | null;
  mcs150Date: string | null;
  mcs150MileageYear: number | null;
  mcs151Mileage: number | null;
  statusCode: string;
  carrierOperation: string | null;
  powerUnits: number | null;
  totalDrivers: number | null;
  safetyRating: string | null;
  safetyRatingDate: string | null;
  // Contact information
  telephone?: string;
  emailAddress?: string;
  fax?: string;
  ein?: number | null;
  dunsNumber?: string | null;
  companyOfficer1?: string | null;
  companyOfficer2?: string | null;
  // Physical address
  physical?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  // Mailing address
  mailing?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  mailingAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  // Fleet information
  fleet?: {
    trucks?: number;
    tractors?: number;
    trailers?: number;
  };
  [key: string]: unknown; // Additional census fields
}

// ============================================================================
// ALERT TYPES
// ============================================================================

/**
 * Alert/notification record
 */
export interface Alert {
  id: number;
  dotNumber: number;
  legalName?: string;
  carrierName: string;
  alertType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  oldValue: string;
  newValue: string;
  details?: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
  readAt?: string;
}

// ============================================================================
// MONITORING TYPES
// ============================================================================

/**
 * Monitored carrier
 */
export interface MonitoredCarrier {
  id: number;
  dotNumber: number;
  legalName: string;
  dbaName: string | null;
  statusCode: string;
  safetyRating: string | null;
  allowedToOperate: string | null;
  totalDrivers: number;
  totalPowerUnits: number;
  addedAt: string;
  lastUpdated: string;
  unreadAlerts?: number;
  isActive?: boolean;
}

// ============================================================================
// STATISTICS TYPES
// ============================================================================

/**
 * Platform statistics
 */
export interface PlatformStats {
  totalUsers: number;
  totalCarriers: number;
  totalBrokers: number;
  totalShippers: number;
  totalDataPoints: number;
  crossValidationRate: number;
  creditsIssued: number;
}

/**
 * System health metrics
 */
export interface SystemHealth {
  apiStatus: 'healthy' | 'degraded' | 'down';
  databaseStatus: 'connected' | 'disconnected';
  cacheHitRate: number;
  errorRate: number;
  uptimeSeconds?: number;
}

/**
 * Admin dashboard data
 */
export interface AdminDashboard {
  platformStats: PlatformStats;
  systemHealth: SystemHealth;
}

// ============================================================================
// SCORING TYPES
// ============================================================================

/**
 * Health score factors
 */
export interface HealthScoreFactors {
  safetyRating: string | null;
  safetyRatingPenalty: number;
  driverOosRate: number;
  driverOosPenalty: number;
  vehicleOosRate: number;
  vehicleOosPenalty: number;
  fatalCrashes: number;
  fatalCrashPenalty: number;
  totalCrashes: number;
  crashPenalty: number;
  allowedToOperate: string;
  operatingPenalty: number;
}

/**
 * SimpleHealthScore - The canonical health score type for frontend display
 */
export interface SimpleHealthScore {
  dotNumber?: number;
  score: number;
  riskLevel?: string;
  grade?: string;
  factors?: HealthScoreFactors;
  components?: {
    safety: number;
    operations: number;
    growth: number;
    financial: number;
  };
  asOfDate?: string;
  cached?: boolean;
}

// ============================================================================
// EXPORT TYPES
// ============================================================================

/**
 * Export format options
 */
export type ExportFormat = 'csv' | 'json' | 'pdf';

/**
 * Export request
 */
export interface ExportRequest {
  dotNumber: number;
  format: ExportFormat;
  includeViolations?: boolean;
  includeInspections?: boolean;
  includeCrashes?: boolean;
  includePredictions?: boolean;
}

// ============================================================================
// BAYESIAN FAIR SCORE TYPES
// ============================================================================

/**
 * Bayesian Fair Score - bias-corrected safety assessment
 */
export interface FairScore {
  dotNumber: number;
  basicId: string;
  basicName: string;
  fairScore: number;
  fairScoreLower: number;
  fairScoreUpper: number;
  credibleIntervalWidth: number;
  confidenceLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  thetaMean: number;
  thetaStd: number;
  biasAdjustments: {
    fleetSize: number;
    region: number;
    operation: number;
    total: number;
  };
  peerGroup: {
    fleetSizeCategory: string;
    fmcsaRegion: number | null;
    operationType: string | null;
    segment: string | null;
  };
  convergence: {
    rhat: number;
    ess: number;
    status: string;
  };
  modelVersion: string;
  calculatedAt: string;
  expiresAt: string | null;
  batchJobId: string | null;
}

/**
 * Fair Score Response with summary
 */
export interface FairScoreResponse {
  dotNumber: number;
  scores: FairScore[];
  summary: {
    overallFairScore: number | null;
    highConfidenceCount: number;
    mediumConfidenceCount: number;
    lowConfidenceCount: number;
    averageCredibleIntervalWidth: number;
  };
  metadata: {
    modelType: string;
    dataSource: string;
    lastUpdated: string | null;
  };
}

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * API Error response
 */
export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
  statusCode?: number;
}

/**
 * Validation error
 */
export interface ValidationError {
  field: string;
  message: string;
  value?: unknown;
}

// ============================================================================
// RESPONSE TYPE DEFINITIONS (Issues #721-735)
// ============================================================================

/**
 * Historic stats response wrapper (Issue #721)
 */
export interface HistoricStatsResponse {
  success: boolean;
  data: {
    dotNumber: number;
    carrierName: string;
    dbaName: string | null;
    addDate: string;
    currentCensus: unknown;
    historicalSnapshots: unknown[];
    totalSnapshots: number;
    changes: unknown;
    oldestSnapshot: unknown;
    newestSnapshot: unknown;
  };
  dotNumber: number;
  source: 'database' | 'cache' | 'fmcsa';
}

/**
 * BASIC scores response with metadata (Issue #722)
 */
export interface BASICScoresResponse {
  success: boolean;
  data: BASICScore[];
  dotNumber: number;
  count: number;
  hasEstimatedScores: boolean;
  source: 'fmcsa' | 'cache' | 'database';
}

/**
 * Search carriers response with nested structure (Issue #723)
 */
export interface SearchCarriersResponse {
  success: boolean;
  data: {
    results: SearchResult[];
    total: number;
    page: number;
    totalPages: number;
    filters: Record<string, string>;
  };
}

/**
 * Alerts response with nested structure (Issue #724)
 */
export interface AlertsResponse {
  success: boolean;
  data: {
    alerts: Alert[];
    unreadCount: number;
    total: number;
  };
}

/**
 * Paginated inspections response (Issue #726)
 */
export interface PaginatedInspectionsResponse {
  success: boolean;
  data: Inspection[];
  dotNumber: number;
  count: number;
  total: number;
  hasMore: boolean;
  limit: number;
  offset: number;
  source: 'fmcsa' | 'cache' | 'database';
  timestamp: string;
}

/**
 * Paginated violations response (Issue #727)
 */
export interface PaginatedViolationsResponse {
  success: boolean;
  data: Violation[];
  dotNumber: number;
  count: number;
  total: number;
  hasMore: boolean;
  limit: number;
  offset: number;
  source: string;
}

/**
 * Merger analysis response (Issue #728)
 */
export interface MergerAnalysisResponse {
  success: boolean;
  data: {
    mergerAnalysis: {
      sourceCarrier: {
        dotNumber: number;
        legalName: string;
        totalViolations: number;
        totalInspections: number;
        basicScores: Record<string, unknown>;
      };
      targetCarrier: {
        dotNumber: number;
        legalName: string;
        totalViolations: number;
        totalInspections: number;
        basicScores: Record<string, unknown>;
      };
      combinedProfile: Record<string, unknown>;
      riskAssessment: Record<string, unknown>;
      basicScoreProjections: unknown[];
      recommendations: string[];
      timestamp: string;
    };
  };
}

/**
 * Census data response (Issue #729)
 */
export interface CensusDataResponse {
  success: boolean;
  data: CensusData;
  source: 'fmcsa' | 'cache';
  lastUpdated: string;
}

/**
 * Observed units response (Issue #730)
 */
export interface ObservedUnitsResponse {
  success: boolean;
  data: {
    units: Array<{
      id: number;
      dotNumber: number;
      unitType: 'truck' | 'trailer' | 'bus' | 'hazmat';
      vinNumber: string;
      licensePlate: string;
      state: string;
      lastInspectionDate: string;
      violations: number;
      oosCount: number;
    }>;
    totalCount: number;
    byType: Record<string, number>;
  };
  dotNumber: number;
  count: number;
}

/**
 * Contact matches response (Issue #731)
 */
export interface ContactMatchesResponse {
  success: boolean;
  data: {
    matches: Array<{
      id: number;
      sourceDotNumber: number;
      matchedDotNumber: number;
      matchType: 'phone' | 'email' | 'address' | 'name';
      matchValue: string;
      confidence: number;
      carrierName: string;
      discovered: string;
    }>;
    totalMatches: number;
    matchTypes: {
      phone: number;
      email: number;
      address: number;
      name: number;
    };
  };
  dotNumber: number;
}

/**
 * Platform stats response (Issue #732)
 */
export interface PlatformStatsResponse {
  success: boolean;
  data: PlatformStats;
  generatedAt: string;
  cacheTTL: number;
}

/**
 * Crashes response with full fields (Issue #733)
 */
export interface CrashesResponse {
  success: boolean;
  data: Crash[];
  dotNumber: number;
  count: number;
  total: number;
}

// ============================================================================
// TYPE GUARDS AND VALIDATION (Issues #766-780)
// ============================================================================

/**
 * API Response wrapper with paginated data
 */
export interface ApiResponseDataWithTotal<T> {
  data: T[];
  total: number;
  count?: number;
  hasMore?: boolean;
}

/**
 * API Response wrapper with data array
 */
export interface ApiResponseData<T> {
  data: T[];
  count?: number;
  total?: number;
  hasMore?: boolean;
}

/**
 * Type guard for CarrierProfile
 */
export function isCarrierProfile(data: unknown): data is CarrierProfile {
  if (typeof data !== 'object' || data === null) return false;
  const profile = data as Record<string, unknown>;
  return (
    typeof profile.dotNumber === 'number' &&
    typeof profile.legalName === 'string' &&
    typeof profile.statusCode === 'string'
  );
}

/**
 * Type guard for CensusData
 */
export function isCensusData(data: unknown): data is CensusData {
  if (typeof data !== 'object' || data === null) return false;
  const census = data as Record<string, unknown>;
  return (
    typeof census.dotNumber === 'number'
  );
}

/**
 * Type guard for InspectionsApiResponse
 */
export function isInspectionsResponse(data: unknown): data is ApiResponseDataWithTotal<Inspection> {
  if (typeof data !== 'object' || data === null) return false;
  const resp = data as Record<string, unknown>;
  return (
    Array.isArray(resp.data) &&
    typeof resp.total === 'number' &&
    (typeof resp.hasMore === 'boolean' || resp.hasMore === undefined)
  );
}

/**
 * Type guard for ViolationsApiResponse
 */
export function isViolationsResponse(data: unknown): data is ApiResponseData<Violation> {
  if (typeof data !== 'object' || data === null) return false;
  const resp = data as Record<string, unknown>;
  return (
    Array.isArray(resp.data) &&
    (typeof resp.count === 'number' || resp.count === undefined)
  );
}

/**
 * Type guard for MergerResponse
 */
export function isMergerResponse(data: unknown): data is { mergerAnalysis: Record<string, unknown> } {
  if (typeof data !== 'object' || data === null) return false;
  const resp = data as Record<string, unknown>;
  return 'mergerAnalysis' in resp && typeof resp.mergerAnalysis === 'object';
}

// ============================================================================
// ISSUE #783: Search Response Type Guards
// ============================================================================

/**
 * Search response with results array (Issue #783)
 */
export interface SearchResponse {
  results: SearchResult[];
  total: number;
  page?: number;
  totalPages?: number;
}

/**
 * Type guard for SearchResponse
 */
export function isSearchResponse(data: unknown): data is SearchResponse {
  if (typeof data !== 'object' || data === null) return false;
  const resp = data as Record<string, unknown>;
  return (
    'results' in resp &&
    Array.isArray(resp.results) &&
    (typeof resp.total === 'number' || resp.total === undefined)
  );
}

// ============================================================================
// ISSUE #784: Historic Stats Type Guards
// ============================================================================

/**
 * Trend data for historic stats
 */
export interface TrendData {
  direction: 'up' | 'down' | 'stable';
  changePercent: number;
  dataPoints: Array<{ date: string; value: number }>;
}

/**
 * Historic snapshot record
 */
export interface HistoricSnapshot {
  date: string;
  basicScores: Record<string, number>;
  violationCount: number;
  inspectionCount: number;
  crashCount: number;
}

/**
 * Historic milestone
 */
export interface Milestone {
  date: string;
  event: string;
  impact: 'positive' | 'negative' | 'neutral';
}

/**
 * Complete historic stats structure (Issue #784)
 */
export interface HistoricStats {
  dotNumber: number;
  snapshots: HistoricSnapshot[];
  trends: {
    violations: TrendData;
    inspections: TrendData;
    crashes: TrendData;
    basicScores: Record<string, TrendData>;
  };
  milestones: Milestone[];
}

/**
 * Type guard for HistoricStats
 */
export function isHistoricStats(data: unknown): data is HistoricStats {
  if (typeof data !== 'object' || data === null) return false;
  const stats = data as Record<string, unknown>;
  return (
    typeof stats.dotNumber === 'number' &&
    'snapshots' in stats &&
    Array.isArray(stats.snapshots) &&
    'trends' in stats &&
    typeof stats.trends === 'object'
  );
}

// ============================================================================
// ISSUE #786: Typed Column Definitions for Tables
// ============================================================================

/**
 * Generic typed column definition for tables (Issue #786)
 */
export interface TypedColumn<T extends Record<string, any>> {
  key: keyof T;
  header: string;
  render?: (value: T[keyof T], item: T) => React.ReactNode;
  accessor?: (item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
}

/**
 * Helper function for type-safe column value extraction (Issue #786)
 */
export function getColumnValue<T extends Record<string, any>>(
  item: T,
  column: TypedColumn<T>
): React.ReactNode {
  if (column.accessor) {
    return column.accessor(item);
  }
  if (column.render) {
    return column.render(item[column.key], item);
  }
  const value = item[column.key];
  return value !== null && value !== undefined ? String(value) : '';
}

// ============================================================================
// ISSUE #787: Leaflet Map Event Types
// ============================================================================

/**
 * Properties stored on map markers
 */
export interface MarkerProperties {
  [key: string]: unknown;
}

/**
 * Options for carrier map markers (Issue #787)
 */
export interface CarrierMarkerOptions {
  data: MarkerProperties;
  [key: string]: unknown;
}

// ============================================================================
// ISSUE #788: Recharts Event Types
// ============================================================================

/**
 * Chart data point base interface
 */
export interface ChartDataPoint {
  [key: string]: string | number | undefined;
}

/**
 * Active payload for Recharts click events
 */
export interface RechartsActivePayload<T = ChartDataPoint> {
  payload: T;
  dataKey: string;
  value: number;
  name: string;
}

/**
 * Recharts click event structure (Issue #788)
 */
export interface RechartsClickEvent<T = ChartDataPoint> {
  activePayload?: RechartsActivePayload<T>[];
  activeLabel?: string;
  activeTooltipIndex?: number;
  chartX?: number;
  chartY?: number;
}

/**
 * Type guard for Recharts click events
 */
export function hasActivePayload<T extends ChartDataPoint = ChartDataPoint>(
  event: unknown
): event is RechartsClickEvent<T> & { activePayload: NonNullable<RechartsClickEvent<T>['activePayload']> } {
  return (
    typeof event === 'object' &&
    event !== null &&
    'activePayload' in event &&
    Array.isArray((event as RechartsClickEvent<T>).activePayload) &&
    (event as RechartsClickEvent<T>).activePayload!.length > 0
  );
}

// ============================================================================
// ISSUE #789: Drill-Down Context Types
// ============================================================================

/**
 * Drill-down levels in the analytics hierarchy
 */
export type DrillDownLevel =
  | 'overview'
  | 'category'
  | 'month'
  | 'state'
  | 'violation'
  | 'crash'
  | 'network';

/**
 * Item in the drill-down navigation path
 */
export interface DrillDownPathItem {
  level: DrillDownLevel;
  label: string;
  data: any;
}

/**
 * Drill-down data union type (can be any of several types)
 */
export type DrillDownData = Record<string, unknown> | null;

/**
 * Complete drill-down state (Issue #789)
 */
export interface DrillDownState {
  data: DrillDownData;
  level: DrillDownLevel;
  path: DrillDownPathItem[];
}

// ============================================================================
// ISSUE #790: Violation Category Detail Component
// ============================================================================

/**
 * Violation groupings by various categories
 */
export interface ViolationGroupings {
  byCode: Record<string, Violation[]>;
  bySeverity: Record<string, Violation[]>;
  byState: Record<string, Violation[]>;
  byMonth?: Record<string, Violation[]>;
}

/**
 * ViolationCategoryDetail props (Issue #790)
 */
export interface ViolationCategoryDetailProps {
  category: string;
  violations: Violation[];
  data: ViolationGroupings;
  onViolationClick?: (violation: Violation) => void;
  onDrillDown?: (groupType: string, groupKey: string) => void;
}

// ============================================================================
// ISSUE #791: Month Detail View Component
// ============================================================================

/**
 * Month summary data
 */
export interface MonthSummary {
  month: string;
  year: number;
  violationCount: number;
  crashCount: number;
  inspectionCount: number;
  oosRate: number;
}

/**
 * Month comparison data
 */
export interface MonthComparison {
  violationsDelta: number;
  crashesDelta: number;
  inspectionsDelta: number;
  violationsPercent: number;
  crashesPercent: number;
  inspectionsPercent: number;
  trend: 'improving' | 'worsening' | 'stable';
}

/**
 * MonthDetailView props (Issue #791)
 */
export interface MonthDetailViewProps {
  month: string;
  year: number;
  violations: Violation[];
  crashes: Crash[];
  inspections: Inspection[];
  previousMonth: MonthSummary | null;
  comparison: MonthComparison;
  onItemClick?: (type: string, item: Violation | Crash | Inspection) => void;
}

// ============================================================================
// ISSUE #792: State Detail View Component
// ============================================================================

/**
 * Geographic location data
 */
export interface GeoLocation {
  id: string;
  latitude: number;
  longitude: number;
  city: string | null;
  county: string | null;
  type: 'inspection' | 'crash';
}

/**
 * Violation code with count
 */
export interface ViolationCodeCount {
  code: string;
  description: string;
  count: number;
}

/**
 * State analytics data
 */
export interface StateAnalytics {
  totalInspections: number;
  totalViolations: number;
  oosRate: number;
  topViolationCodes: ViolationCodeCount[];
  inspectionsByCity: Record<string, number>;
  trend: 'up' | 'down' | 'stable';
}

/**
 * StateDetailView props (Issue #792)
 */
export interface StateDetailViewProps {
  state: string;
  stateName: string;
  violations: Violation[];
  inspections: Inspection[];
  stateAnalytics: StateAnalytics;
  locations: GeoLocation[];
  onLocationClick?: (location: GeoLocation) => void;
  onItemClick?: (type: string, item: Violation | Inspection) => void;
}

// ============================================================================
// ISSUE #793: Crash Severity Detail View Component
// ============================================================================

/**
 * Monthly count data
 */
export interface MonthlyCount {
  month: string;
  count: number;
}

/**
 * Condition breakdown for crashes
 */
export interface ConditionBreakdown {
  weather: Record<string, number>;
  road: Record<string, number>;
  light: Record<string, number>;
}

/**
 * Severity analytics data
 */
export interface SeverityAnalytics {
  totalCrashes: number;
  totalFatalities: number;
  totalInjuries: number;
  byState: Record<string, number>;
  byMonth: MonthlyCount[];
  byCondition: ConditionBreakdown;
  averageVictims: number;
}

/**
 * CrashSeverityDetailView props (Issue #793)
 */
export interface CrashSeverityDetailViewProps {
  severityLevel: string;
  crashes: Crash[];
  severityAnalytics: SeverityAnalytics;
  onCrashClick?: (crash: Crash) => void;
  onLocationClick?: (crash: Crash) => void;
}

// ============================================================================
// ISSUE #794: Carrier Network Detail Component
// ============================================================================

/**
 * Network connection data
 */
export interface NetworkConnection {
  connectedNode: NetworkNode;
  edge: NetworkEdge;
  depth: number;
}

/**
 * Item in network path
 */
export interface NetworkPathItem {
  node: NetworkNode;
  edge: NetworkEdge | null;
}

/**
 * CarrierNetworkDetail props (Issue #794)
 */
export interface CarrierNetworkDetailProps {
  node: NetworkNode;
  connections: NetworkConnection[];
  networkPath: NetworkPathItem[];
  onNodeClick?: (node: NetworkNode) => void;
  onExpandNetwork?: (dotNumber: number) => void;
}

// ============================================================================
// ISSUE #795: Filter Preset Types
// ============================================================================

/**
 * Filterable data types
 */
export type FilterableData = Violation | Crash | Inspection | SearchResult;

/**
 * Active filters structure
 */
export interface ActiveFilters<T extends FilterableData = FilterableData> {
  [key: string]: unknown;
}

/**
 * Filter preset configuration
 */
export interface FilterPreset<T extends FilterableData = FilterableData> {
  id: string;
  name: string;
  description: string;
  filters: ActiveFilters<T>;
  isDefault?: boolean;
  isUserDefined?: boolean;
  createdAt?: string;
}

/**
 * FilterPresets component props (Issue #795)
 */
export interface FilterPresetsProps<T extends FilterableData = FilterableData> {
  dataType: 'violation' | 'inspection' | 'crash' | 'alert';
  presets: FilterPreset<T>[];
  activePreset: string | null;
  onApplyPreset: (filters: ActiveFilters<T>) => void;
  onSavePreset?: (name: string, filters: ActiveFilters<T>) => void;
  onDeletePreset?: (presetId: string) => void;
}

// ============================================================================
// BASIC Score Type
// ============================================================================

/**
 * BASIC Score record
 */
export interface BASICScore {
  // Primary identifiers
  basicId?: string;
  basic_id?: string;  // Snake_case alias
  basicsId?: number;
  // Category/name fields
  basicName?: string;
  basicCategory?: string;
  basicsShortDesc?: string;
  basicsCode?: string;
  category?: string;
  // Score values
  score?: number | null;
  percentile?: number | null;
  basicsPercentile?: string | number;
  measure?: string;
  measureValue?: string | number | null;
  measure_label?: string;
  // Threshold
  threshold?: string | number | null;
  basicsViolationThreshold?: string | number;
  exceeds?: boolean;
  // Statistics
  totalInspections?: number | null;
  totalViolations?: number | null;
  totalInspectionsWithViolation?: number | null;
  oosRate?: number | null;
  nationalAverage?: number | null;
  // Estimation flag
  isEstimated?: boolean;
  estimated?: boolean;
  // Nested basic data
  basic?: {
    basicsPercentile?: string | number;
    basicsViolationThreshold?: string | number;
    measureValue?: string | number;
    estimated?: boolean;
    [key: string]: unknown;
  };
  // Run date for tracking
  runDate?: Date | string;
  exceededThreshold?: number;
  // Allow additional properties
  [key: string]: unknown;
}

export default ApiResponse;
